/**
 * Lucide Icons Utility for Vanilla TypeScript
 *
 * This module provides a clean interface for rendering Lucide icons
 * in the Wyden Expenditure Approval application.
 */

import {
  createElement,
  // Commonly used icons for approval workflows
  Check,
  X,
  Plus,
  Minus,
  Edit,
  Trash2,
  Save,
  Upload,
  Download,
  FileText,
  File,
  Folder,
  FolderOpen,
  Camera,
  // Navigation and UI
  ChevronUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  ArrowDown,
  Menu,
  Expand,
  History,
  Inbox,
  FileX,
  Home,
  Settings,
  Search,
  Filter,
  SortAsc,
  SortDesc,
  LayoutDashboard,
  Columns3,
  Timer,
  // User and auth
  User,
  Users,
  UserPlus,
  UserCheck,
  LogIn,
  LogOut,
  Lock,
  Unlock,
  Shield,
  ShieldCheck,
  // Status indicators
  AlertCircle,
  AlertTriangle,
  Info,
  CheckCircle,
  XCircle,
  Clock,
  Calendar,
  // Actions
  Send,
  Mail,
  Bell,
  BellRing,
  Eye,
  EyeOff,
  Copy,
  Clipboard,
  ClipboardList,
  ExternalLink,
  Link,
  // Financial/approval specific
  DollarSign,
  Euro,
  CreditCard,
  Receipt,
  Banknote,
  Wallet,
  TrendingUp,
  TrendingDown,
  PieChart,
  BarChart3,
  // Misc
  Loader2,
  RefreshCw,
  MoreHorizontal,
  MoreVertical,
  Paperclip,
  Image,
  Building2,
  Briefcase,
  Plane,
  // Social media icons
  Share2,
  Twitter,
  Facebook,
  Linkedin,
  // Admin settings
  Sliders,
  UserMinus,
  Infinity,
  Sparkles,
} from 'lucide';

/**
 * Icon size variants following the design system
 */
export type IconSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

/**
 * Icon size mapping in pixels
 */
const ICON_SIZES: Record<IconSize, number> = {
  xs: 12,
  sm: 16,
  md: 20,
  lg: 24,
  xl: 32,
};

/**
 * Configuration options for icon rendering
 */
export interface IconOptions {
  /** Icon size variant (default: 'md') */
  size?: IconSize;
  /** Custom size in pixels (overrides size variant) */
  customSize?: number;
  /** CSS class names to apply */
  className?: string;
  /** Stroke width (default: 2) */
  strokeWidth?: number;
  /** Color (uses currentColor by default for dark theme compatibility) */
  color?: string;
  /** Accessibility label */
  ariaLabel?: string;
  /** Whether icon is decorative (hidden from screen readers) */
  decorative?: boolean;
}

/**
 * All available icons exported from lucide
 */
export const icons = {
  // Commonly used icons for approval workflows
  check: Check,
  x: X,
  plus: Plus,
  minus: Minus,
  edit: Edit,
  trash: Trash2,
  save: Save,
  upload: Upload,
  download: Download,
  fileText: FileText,
  file: File,
  folder: Folder,
  folderOpen: FolderOpen,
  camera: Camera,
  // Navigation and UI
  chevronUp: ChevronUp,
  chevronDown: ChevronDown,
  chevronLeft: ChevronLeft,
  chevronRight: ChevronRight,
  arrowLeft: ArrowLeft,
  arrowRight: ArrowRight,
  arrowUp: ArrowUp,
  arrowDown: ArrowDown,
  menu: Menu,
  expand: Expand,
  history: History,
  inbox: Inbox,
  fileX: FileX,
  home: Home,
  settings: Settings,
  search: Search,
  filter: Filter,
  sortAsc: SortAsc,
  sortDesc: SortDesc,
  layoutDashboard: LayoutDashboard,
  columns: Columns3,
  // User and auth
  user: User,
  users: Users,
  userPlus: UserPlus,
  userCheck: UserCheck,
  logIn: LogIn,
  logOut: LogOut,
  lock: Lock,
  unlock: Unlock,
  shield: Shield,
  shieldCheck: ShieldCheck,
  // Status indicators
  alertCircle: AlertCircle,
  alertTriangle: AlertTriangle,
  info: Info,
  checkCircle: CheckCircle,
  xCircle: XCircle,
  clock: Clock,
  timer: Timer,
  calendar: Calendar,
  // Actions
  send: Send,
  mail: Mail,
  bell: Bell,
  bellRing: BellRing,
  eye: Eye,
  eyeOff: EyeOff,
  copy: Copy,
  clipboard: Clipboard,
  clipboardList: ClipboardList,
  externalLink: ExternalLink,
  link: Link,
  // Financial/approval specific
  dollarSign: DollarSign,
  euro: Euro,
  creditCard: CreditCard,
  receipt: Receipt,
  banknote: Banknote,
  wallet: Wallet,
  trendingUp: TrendingUp,
  trendingDown: TrendingDown,
  pieChart: PieChart,
  barChart: BarChart3,
  // Misc
  loader: Loader2,
  refresh: RefreshCw,
  moreHorizontal: MoreHorizontal,
  moreVertical: MoreVertical,
  paperclip: Paperclip,
  image: Image,
  building: Building2,
  briefcase: Briefcase,
  plane: Plane,
  // Social media icons
  share: Share2,
  twitter: Twitter,
  facebook: Facebook,
  linkedin: Linkedin,
  // Admin settings
  sliders: Sliders,
  userMinus: UserMinus,
  infinity: Infinity,
  sparkles: Sparkles,
} as const;

/**
 * Icon name type derived from available icons
 */
export type IconName = keyof typeof icons;

/**
 * Render a Lucide icon as an SVG element
 *
 * @param name - The icon name
 * @param options - Configuration options
 * @returns SVGElement
 *
 * @example
 * ```ts
 * // Basic usage
 * const icon = renderIcon('check');
 * container.appendChild(icon);
 *
 * // With size variant
 * const largeIcon = renderIcon('plus', { size: 'lg' });
 *
 * // With custom options
 * const customIcon = renderIcon('edit', {
 *   size: 'md',
 *   className: 'text-accent',
 *   strokeWidth: 1.5,
 *   ariaLabel: 'Edit item'
 * });
 * ```
 */
export function renderIcon(name: IconName, options: IconOptions = {}): SVGElement {
  const {
    size = 'md',
    customSize,
    className = '',
    strokeWidth = 2,
    color = 'currentColor',
    ariaLabel,
    decorative = false,
  } = options;

  const iconComponent = icons[name];
  if (!iconComponent) {
    console.warn(`Icon "${name}" not found`);
    // Return an empty SVG element
    const emptySvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    return emptySvg;
  }

  const pixelSize = customSize ?? ICON_SIZES[size];

  // Create the SVG element using lucide's createElement
  const svgElement = createElement(iconComponent) as SVGElement;

  // Set size attributes
  svgElement.setAttribute('width', String(pixelSize));
  svgElement.setAttribute('height', String(pixelSize));
  svgElement.setAttribute('stroke-width', String(strokeWidth));
  svgElement.setAttribute('stroke', color);

  // Add classes for styling (important for dark theme compatibility)
  const baseClasses = 'lucide-icon shrink-0';
  svgElement.setAttribute('class', className ? `${baseClasses} ${className}` : baseClasses);

  // Accessibility attributes
  if (decorative) {
    svgElement.setAttribute('aria-hidden', 'true');
    svgElement.setAttribute('focusable', 'false');
  } else if (ariaLabel) {
    svgElement.setAttribute('aria-label', ariaLabel);
    svgElement.setAttribute('role', 'img');
  } else {
    // Default to decorative if no label provided
    svgElement.setAttribute('aria-hidden', 'true');
    svgElement.setAttribute('focusable', 'false');
  }

  return svgElement;
}

/**
 * Render an icon and return it as an HTML string
 * Useful for inserting icons into innerHTML
 *
 * @param name - The icon name
 * @param options - Configuration options
 * @returns HTML string of the SVG
 *
 * @example
 * ```ts
 * button.innerHTML = `${iconHtml('plus', { size: 'sm' })} Add Item`;
 * ```
 */
export function iconHtml(name: IconName, options: IconOptions = {}): string {
  const svgElement = renderIcon(name, options);
  return svgElement.outerHTML;
}

/**
 * Create an icon button element
 *
 * @param iconName - The icon name
 * @param options - Button and icon options
 * @returns HTMLButtonElement with the icon
 *
 * @example
 * ```ts
 * const deleteBtn = createIconButton('trash', {
 *   ariaLabel: 'Delete item',
 *   className: 'btn-danger',
 *   onClick: () => handleDelete()
 * });
 * ```
 */
export function createIconButton(
  iconName: IconName,
  options: IconOptions & {
    buttonClassName?: string;
    onClick?: (event: MouseEvent) => void;
    disabled?: boolean;
    title?: string;
  } = {}
): HTMLButtonElement {
  const { buttonClassName = '', onClick, disabled = false, title, ...iconOptions } = options;

  const button = document.createElement('button');
  button.type = 'button';
  button.className = `icon-button ${buttonClassName}`.trim();
  button.disabled = disabled;

  if (title) {
    button.title = title;
  }

  if (iconOptions.ariaLabel) {
    button.setAttribute('aria-label', iconOptions.ariaLabel);
  }

  // Always make the icon inside a button decorative
  const icon = renderIcon(iconName, { ...iconOptions, decorative: true });
  button.appendChild(icon);

  if (onClick) {
    button.addEventListener('click', onClick);
  }

  return button;
}

/**
 * Create a loading spinner icon with animation
 *
 * @param options - Icon options
 * @returns SVGElement with spinning animation
 *
 * @example
 * ```ts
 * const spinner = createSpinner({ size: 'lg' });
 * loadingContainer.appendChild(spinner);
 * ```
 */
export function createSpinner(options: Omit<IconOptions, 'className' | 'ariaLabel' | 'decorative'> & { className?: string } = {}): SVGElement {
  const { className = '', ...restOptions } = options;
  return renderIcon('loader', {
    ...restOptions,
    className: `animate-spin ${className}`.trim(),
    decorative: true,
  });
}

/**
 * Get the pixel size for a given size variant
 *
 * @param size - Size variant
 * @returns Size in pixels
 */
export function getIconSize(size: IconSize): number {
  return ICON_SIZES[size];
}

/**
 * All available size variants
 */
export const iconSizes = Object.keys(ICON_SIZES) as IconSize[];

/**
 * All available icon names
 */
export const iconNames = Object.keys(icons) as IconName[];
